/**
 * Set up step: the reproduction's own settings, per kind.
 *
 * Also where the uploaded input file can be downloaded again under its original
 * name. A final reproduction (the Marking step was entered) is sent straight on
 * to the Marking step instead.
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField, Stack, MenuItem, Typography, Box, Alert, Card, CardActionArea,
  CardContent, Chip, Button, CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import {
  sidebarForType, REPRODUCTION_MODES, reproductionListRoute, logoComponentsLabel, logoComponentCount,
  REPRODUCTION_KINDS, reproductionKindSlug,
} from "../../helper/reproductionFlow";
import { downloadReproductionSource } from "../../helper/reproductionSource";
import { selectionKind } from "../../helper/selectionFlow";
import { useReproductionParams } from "../../helper/entityRoutes";

// Step 1: name/description, laser + substrate and the palette selection the
// reproduction is based on. Logo reproductions additionally pick the
// reproduction mode; image reproductions configure the separation instead
// (iterations, lightness mapping; the target size is chosen per run on the
// Marking step); gradient reproductions pick their colors on the next step
// and their size / position on the Marking step. A final reproduction (the
// Marking step was entered) lands on the Marking step instead.
export default function ColorReproductionSetup() {
  const navigate = useNavigate();
  // The reproduction and its kind come from the URL; the stored type of the
  // loaded row wins over the URL's kind for the routes built here.
  const { reproductionId, reproductionType: urlType, stepRoute } = useReproductionParams();

  const [errorMessage, setErrorMessage] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [laserDevices, setLaserDevices] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [laserDeviceId, setLaserDeviceId] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [reproductionMode, setReproductionMode] = useState("");
  const [paletteSelectionId, setPaletteSelectionId] = useState("");
  const [selections, setSelections] = useState([]);
  const [selectionsLoading, setSelectionsLoading] = useState(false);
  // Parsed logo content (shape / line / layer counts); the components are
  // derived when Next fixes the mode, so a new upload has none yet.
  const [parsedCounts, setParsedCounts] = useState(null);
  const [reproductionType, setReproductionType] = useState(urlType);
  // Image-reproduction settings (the ImageReproduction setup row).
  const [iterations, setIterations] = useState("150");
  const [lightnessMapping, setLightnessMapping] = useState("linear");
  const [saving, setSaving] = useState(false);
  // Distinguishes the prefill from user-driven laser/substrate changes: only
  // the latter clear the picked palette selection.
  const prefillDoneRef = useRef(false);
  // The uploaded input file (logo SVG / image / shape), downloadable here.
  const [originalFileName, setOriginalFileName] = useState("");
  const [downloadingInput, setDownloadingInput] = useState(false);
  const downloadInputFile = async () => {
    setDownloadingInput(true);
    setErrorMessage(null);
    try {
      await downloadReproductionSource(reproductionId, originalFileName);
    } catch (error) {
      console.error("Error downloading the input file:", error);
      setErrorMessage("Downloading the input file failed.");
    } finally {
      setDownloadingInput(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.post("api/reproduction/get_reproduction_details", {
          reproduction_id: reproductionId,
        });
        const details = response.data;
        if (details.setup_final) {
          navigate(stepRoute("marking", details.reproduction_type || urlType), { replace: true });
          return;
        }
        setName(details.name || "");
        setOriginalFileName(details.original_file_name || "");
        setDescription(details.description || "");
        setLaserDeviceId(details.laser_device_id ?? details.laser_device ?? "");
        setMaterialId(details.material_id ?? details.material ?? "");
        setReproductionMode(details.logo?.reproduction_mode || details.reproduction_mode || "");
        setPaletteSelectionId(details.palette_selection_id ?? details.palette_selection ?? "");
        setParsedCounts({
          shape_count: details.shape_count, line_count: details.line_count, layer_count: details.layer_count,
        });
        if (details.reproduction_type) setReproductionType(details.reproduction_type);
        const imageConfig = details.image || {};
        if (imageConfig.iterations !== null && imageConfig.iterations !== undefined) {
          setIterations(String(imageConfig.iterations));
        }
        if (imageConfig.lightness_mapping) {
          setLightnessMapping(imageConfig.lightness_mapping);
        }
      } catch (error) {
        console.error("Error loading reproduction details:", error);
        setErrorMessage("Loading the reproduction failed.");
      } finally {
        prefillDoneRef.current = true;
      }
    };
    load();
    axios
      .get("api/color/explorer/laser/device")
      .then((res) => {
        if (Array.isArray(res.data)) {
          setLaserDevices(res.data.map((item) => ({ id: item.id, name: item.laser_device_name })));
        }
      })
      .catch((err) => console.error("Error fetching lasers: " + err));
    axios
      .get("api/color/explorer/material")
      .then((res) => {
        if (Array.isArray(res.data)) {
          setMaterials(res.data.map((item) => ({ id: item.id, name: item.material_name })));
        }
      })
      .catch((err) => console.error("Error fetching materials: " + err));
  }, []);

  // Load the palette selections that exist for the chosen laser + substrate.
  useEffect(() => {
    if (!laserDeviceId || !materialId) {
      setSelections([]);
      return;
    }
    let cancelled = false;
    const loadSelections = async () => {
      setSelectionsLoading(true);
      try {
        // Image reproductions take image selections only; logo and gradient
        // reproductions logo and image selections.
        const response = await axios.post("api/reproduction/get_selections_list", {
          laser_device_id: laserDeviceId,
          material_id: materialId,
          reproduction_type: reproductionType,
        });
        if (!cancelled) setSelections(response.data.selections || []);
      } catch (error) {
        console.error("Error loading palette selections:", error);
        if (!cancelled) {
          setSelections([]);
          setErrorMessage("Loading the palette selections failed.");
        }
      } finally {
        if (!cancelled) setSelectionsLoading(false);
      }
    };
    loadSelections();
    return () => { cancelled = true; };
  }, [laserDeviceId, materialId, reproductionType]);

  const handleLaserChange = (event) => {
    setLaserDeviceId(event.target.value);
    if (prefillDoneRef.current) setPaletteSelectionId("");
  };
  const handleMaterialChange = (event) => {
    setMaterialId(event.target.value);
    if (prefillDoneRef.current) setPaletteSelectionId("");
  };

  const isImageReproduction = reproductionType === "IMAGE_REPRODUCTION";
  const isGradientReproduction = reproductionType === "GRADIENT_REPRODUCTION";
  const parsedComponentCount = logoComponentCount(parsedCounts);

  const nextPageClickFunc = async (navigatePageName) => {
    setSaving(true);
    setErrorMessage(null);
    try {
      if (isGradientReproduction) {
        await axios.post("api/reproduction/update_reproduction", {
          reproduction_id: reproductionId,
          name,
          description,
          material_id: materialId,
          laser_device_id: laserDeviceId,
          palette_selection_id: paletteSelectionId,
        });
        // The colors of the blend are picked on the Gradient step, the size
        // and position of the shape on the Marking step.
      } else if (isImageReproduction) {
        await axios.post("api/reproduction/update_reproduction", {
          reproduction_id: reproductionId,
          name,
          description,
          material_id: materialId,
          laser_device_id: laserDeviceId,
          palette_selection_id: paletteSelectionId,
          ...(iterations === "" ? {} : { iterations: Number(iterations) }),
          lightness_mapping: lightnessMapping,
        });
        // No set_up_reproduction for images: the halftoning assigns the
        // palette colors itself during the computation.
      } else {
        await axios.post("api/reproduction/update_reproduction", {
          reproduction_id: reproductionId,
          name,
          description,
          material_id: materialId,
          laser_device_id: laserDeviceId,
          palette_selection_id: paletteSelectionId,
          reproduction_mode: reproductionMode,
        });
        // Auto-assigns palette colors to the parsed components.
        await axios.post("api/reproduction/set_up_reproduction", {
          reproduction_id: reproductionId,
        });
      }
      navigate(navigatePageName);
    } catch (error) {
      console.error("Error saving the reproduction setup:", error);
      const detail = error?.response?.data?.error;
      setErrorMessage(detail || "Saving the reproduction setup failed.");
    } finally {
      setSaving(false);
    }
  };

  const nextDisabled = !(laserDeviceId && materialId && paletteSelectionId
    && (isImageReproduction || isGradientReproduction || reproductionMode));
  const nextPage = stepRoute(
    isGradientReproduction ? "gradient" : (isImageReproduction ? "gamut" : "edit"),
    reproductionType,
  );
  const subtitle = isGradientReproduction
    ? "Describe the reproduction, pick the laser/substrate combination and the palette selection."
    : (isImageReproduction
      ? "Describe the reproduction, pick the laser/substrate combination, the image settings and the palette selection."
      : "Describe the reproduction, pick the laser/substrate combination, the reproduction mode and the palette selection.");

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="Set up"
        subtitle={subtitle}
        stepSidebar={sidebarForType(
          reproductionType, 0,
          isImageReproduction || isGradientReproduction ? null : parsedComponentCount || null,
        )}
        footerBelowPaper={
          <Box sx={{ width: "100%" }}>
            <NavBarBottom
              variant="embedded"
              returnLabel="Back"
              prevPage={reproductionListRoute(reproductionType)}
              nextPage={nextPage}
              enableNextPage={nextDisabled}
              nextLoading={saving}
              nextPageClickFunc={nextPageClickFunc}
            />
          </Box>
        }
      >
        <Stack spacing={2.5} sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
          {errorMessage && (
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          {originalFileName ? (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, wordBreak: "break-word" }}>
                {`${REPRODUCTION_KINDS[reproductionKindSlug(reproductionType)].fileLabel}: ${originalFileName}`}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={downloadingInput ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={downloadingInput}
                onClick={downloadInputFile}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Input file
              </Button>
            </Stack>
          ) : null}

          {!isImageReproduction && !isGradientReproduction && parsedComponentCount > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {`Parsed logo content: ${logoComponentsLabel(parsedCounts)}`}
            </Typography>
          ) : null}

          <TextField
            required
            label="Reproduction name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              fullWidth
              label="Laser system"
              value={laserDeviceId}
              onChange={handleLaserChange}
            >
              {laserDevices.map((laserSystem) => (
                <MenuItem key={laserSystem.id} value={laserSystem.id}>{laserSystem.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Substrate"
              value={materialId}
              onChange={handleMaterialChange}
            >
              {materials.map((materialRow) => (
                <MenuItem key={materialRow.id} value={materialRow.id}>{materialRow.name}</MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* Image reproductions configure the halftoning; logos pick a
              reproduction mode; gradients have nothing to set here. */}
          {isGradientReproduction ? null : isImageReproduction ? (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Image settings
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Iterations"
                  type="number"
                  value={iterations}
                  onChange={(event) => setIterations(event.target.value)}
                  helperText="More iterations = better separation, longer computation. Default 150."
                />
                <TextField
                  select
                  fullWidth
                  label="Lightness mapping"
                  value={lightnessMapping}
                  onChange={(event) => setLightnessMapping(event.target.value)}
                >
                  <MenuItem value="linear">Linear (recommended)</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                </TextField>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                The image size is set per instance on the Marking step.
              </Typography>
            </Stack>
          </Box>
          ) : (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Reproduction mode
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
              }}
            >
              {REPRODUCTION_MODES.map((mode) => {
                const selected = reproductionMode === mode.value;
                return (
                  <Card
                    key={mode.value}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: selected ? "primary.main" : "rgba(15, 23, 42, 0.12)",
                      borderWidth: selected ? 2 : 1,
                      bgcolor: selected ? "rgba(76, 126, 255, 0.04)" : "background.paper",
                      height: "100%",
                    }}
                  >
                    <CardActionArea
                      onClick={() => setReproductionMode(mode.value)}
                      sx={{ height: "100%", alignItems: "stretch" }}
                    >
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {mode.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {mode.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          </Box>
          )}

          {/* Palette selection cards. */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Palette selection
            </Typography>
            {reproductionType === "GRADIENT_REPRODUCTION" && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                A logo selection's colors may differ in cluster width. The halftone cell takes the widest and stacks the narrower ones.
              </Typography>
            )}
            {!laserDeviceId || !materialId ? (
              <Typography variant="body2" color="text.secondary">
                Choose a laser system and a substrate first to see the available palette selections.
              </Typography>
            ) : selectionsLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading palette selections...
              </Typography>
            ) : selections.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {reproductionType === "IMAGE_REPRODUCTION"
                  ? "No finalized image selection exists for this laser and substrate."
                  : "No finalized logo or image selection exists for this laser and substrate."}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                }}
              >
                {selections.map((selection) => {
                  const selected = paletteSelectionId === selection.id;
                  return (
                    <Card
                      key={selection.id}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderColor: selected ? "primary.main" : "rgba(15, 23, 42, 0.12)",
                        borderWidth: selected ? 2 : 1,
                        bgcolor: selected ? "rgba(76, 126, 255, 0.04)" : "background.paper",
                        height: "100%",
                      }}
                    >
                      <CardActionArea
                        onClick={() => setPaletteSelectionId(selection.id)}
                        sx={{ height: "100%", alignItems: "stretch" }}
                      >
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, wordBreak: "break-word", minWidth: 0 }}
                            >
                              {selection.name}
                            </Typography>
                            <Chip size="small" label={`${selection.sample_count} colors`} />
                          </Stack>
                          {/* A logo reproduction lists logo and image selections side by side. */}
                          {selection.selection_type ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={selectionKind(selection.selection_type).typeLabel}
                              sx={{ mt: 0.75, fontWeight: 600 }}
                            />
                          ) : null}
                          {selection.description ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {selection.description}
                            </Typography>
                          ) : null}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Stack>
      </ExplorationPageShell>
    </Stack>
  );
}
